import React, { useState, useEffect, useRef, useContext } from "react";
import { UserContext } from "../../Context/UserContext";
import axios from "axios";
import { FaUserCircle, FaCheckCircle, FaLock, FaKey, FaBoxOpen, FaPrint, FaExclamationTriangle, FaArrowLeft, FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import DeliveryMap from "../Map/DeliveryMap";
import { io } from "socket.io-client";

const DeliveryPanel = () => {
  const { user } = useContext(UserContext);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [secretKey, setSecretKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [baseUrl] = useState(window.API_URL || "");
  const [printSettings, setPrintSettings] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const [activeTab, setActiveTab] = useState('assigned'); // 'assigned' | 'delivered' | 'pool'
  const [pooledOrders, setPooledOrders] = useState([]);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [deliveredLoading, setDeliveredLoading] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterInvoiceNo, setFilterInvoiceNo] = useState("");
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedMapAddress, setSelectedMapAddress] = useState("");
  const [selectedMapExactLocation, setSelectedMapExactLocation] = useState(null);
  const [currentDriverLocation, setCurrentDriverLocation] = useState(null);
  const [restaurantLocation, setRestaurantLocation] = useState(null);

  const [showCashModal, setShowCashModal] = useState(false);
  const [cashAmount, setCashAmount] = useState("");
  const [currentOrderForCash, setCurrentOrderForCash] = useState(null);

  const [activeOrderIdForMap, setActiveOrderIdForMap] = useState(null);
  const [resolvedCustomerLocations, setResolvedCustomerLocations] = useState({});

  const calculateDistance = (loc1, loc2) => {
    if (!loc1 || !loc2) return null;
    const R = 6371e3; // metres
    const lat1 = loc1.lat * Math.PI / 180;
    const lat2 = loc2.lat * Math.PI / 180;
    const deltaLat = (loc2.lat - loc1.lat) * Math.PI / 180;
    const deltaLng = (loc2.lng - loc1.lng) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const getCustomerCoordinates = (order) => {
    if (order.deliveryAddress && typeof order.deliveryAddress === 'object') {
      if (order.deliveryAddress.latitude && order.deliveryAddress.longitude) {
        return { lat: parseFloat(order.deliveryAddress.latitude), lng: parseFloat(order.deliveryAddress.longitude) };
      }
    }
    return null;
  };

  const getOrderKitchenStatus = (order) => {
    if (!order.cartItems || order.cartItems.length === 0) return "Pending";
    
    let allPickedUp = true;
    let allPending = true;
    let hasPrepared = false;
    let hasPreparing = false;
    let totalStatusEntries = 0;

    order.cartItems.forEach(item => {
      const statuses = Object.values(item.kitchenStatuses || {});
      if (statuses.length === 0) {
        allPickedUp = false;
      } else {
        statuses.forEach(status => {
          totalStatusEntries++;
          if (status !== 'PickedUp') allPickedUp = false;
          if (status !== 'Pending') allPending = false;
          if (status === 'Prepared') hasPrepared = true;
          if (status === 'Preparing') hasPreparing = true;
        });
      }
    });

    if (totalStatusEntries === 0 || allPending) return "Pending";
    if (allPickedUp) return "Ready";
    return "Preparing";
  };

  const socketRef = useRef(null);
  const watchIdRef = useRef(null);
  const isSimulatingRef = useRef(false);

  // Live Tracking Effect
  useEffect(() => {
    // Only track if authenticated and has active assigned orders
    if (isAuthenticated && assignedOrders.length > 0) {
      if (!socketRef.current) {
        // Fallback to window.location.origin if baseUrl is empty
        const socketUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
        socketRef.current = io(socketUrl, { transports: ['polling', 'websocket'] });
      }

      if (navigator.geolocation) {
        // Force an immediate location fetch first to sync with current assignedOrders state
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentDriverLocation({ lat: latitude, lng: longitude });
            assignedOrders.forEach(order => {
              if (order.status === "picked_up" && socketRef.current) {
                socketRef.current.emit('driver_location_update', {
                  orderId: order.orderId,
                  lat: latitude,
                  lng: longitude
                });
              }
            });
          },
          (error) => {
            console.error("Error getting initial location:", error);
            if (error.code === error.PERMISSION_DENIED) {
              alert("Location permission denied. Please allow location access.");
            }
          },
          { enableHighAccuracy: true, timeout: 15000 }
        );
      } else {
        alert("Geolocation is blocked! You must use 'localhost' or an 'HTTPS' connection to track driver location.");
      }

      if (!watchIdRef.current && navigator.geolocation) {
        // Then start watching for continuous updates
        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            if (isSimulatingRef.current) return; // Ignore real GPS if testing via map drag
            
            const { latitude, longitude } = position.coords;
            setCurrentDriverLocation({ lat: latitude, lng: longitude });
            // Emit location ONLY for active orders marked as picked_up
            assignedOrders.forEach(order => {
              if (order.status === "picked_up" && socketRef.current) {
                socketRef.current.emit('driver_location_update', {
                  orderId: order.orderId,
                  lat: latitude,
                  lng: longitude
                });
              }
            });
          },
          (error) => {
            console.error("Error watching location:", error);
          },
          { enableHighAccuracy: true, maximumAge: 0 }
        );
      }
    } else {
      // Stop tracking if no active orders or logged out
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    }

    return () => {
      // Cleanup on unmount
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, assignedOrders, baseUrl]);

  useEffect(() => {
    if (isAuthenticated && selectedEmployee) {
      if (!socketRef.current) {
        const socketUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
        socketRef.current = io(socketUrl, { transports: ['polling', 'websocket'] });
      }
      
      const handleNewPooledOrder = (data) => {
        if ((!data.company || data.company === selectedEmployee.company) && 
            (!data.branch || data.branch === selectedEmployee.branch)) {
          fetchPooledOrders();
          setNewOrderAlert(data);
          setActiveTab('pool');
          // Play simple beep or notification sound
          try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 note
            gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
            oscillator.start();
            oscillator.stop(audioCtx.currentTime + 0.3); // Play for 300ms
          } catch(e) {
             console.log("Audio not supported or blocked");
          }
        }
      };

      const handleOrderAssigned = (data) => {
        const idToRemove = data.order_id || data.invoice_no;
        if (idToRemove) {
          setPooledOrders(prev => prev.filter(o => o.invoice_no !== idToRemove && o.orderId !== idToRemove && o._id !== idToRemove));
        }
      };

      const handleOrderCancelled = (data) => {
        const idToRemove = data.order_id || data.invoice_no;
        if (idToRemove) {
          setPooledOrders(prev => prev.filter(o => o.invoice_no !== idToRemove && o.orderId !== idToRemove && o._id !== idToRemove));
        }
      };

      const handleOrderUpdate = (data) => {
        if (activeTab === 'assigned') {
          fetchOrders(selectedEmployee.employeeId);
        } else if (activeTab === 'pool') {
          fetchPooledOrders();
        }
      };

      socketRef.current.on('new_pooled_order', handleNewPooledOrder);
      socketRef.current.on('order_assigned', handleOrderAssigned);
      socketRef.current.on('pooled_order_cancelled', handleOrderCancelled);
      socketRef.current.on('order_update', handleOrderUpdate);

      return () => {
        if (socketRef.current) {
          socketRef.current.off('new_pooled_order', handleNewPooledOrder);
          socketRef.current.off('order_assigned', handleOrderAssigned);
          socketRef.current.off('pooled_order_cancelled', handleOrderCancelled);
          socketRef.current.off('order_update', handleOrderUpdate);
        }
      };
    }
  }, [isAuthenticated, selectedEmployee, baseUrl]);

  useEffect(() => {
    const fetchPrintSettings = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/print_settings/active`);
        if (response.data) {
          setPrintSettings(response.data);
        }
      } catch (error) {
        console.error("Error fetching print settings:", error);
      }
    };
    fetchPrintSettings();
  }, [baseUrl]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/delivery-employees`);
        if (response.data && Array.isArray(response.data)) {
          setEmployees(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch employees", err);
      }
    };
    fetchEmployees();
  }, [baseUrl]);

  // Fetch orders for authenticated employee
  const fetchOrders = async (employeeId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/activeorders`);
      if (Array.isArray(response.data)) {
        // Filter orders assigned to this delivery person and NOT yet completed
        const orders = response.data.filter(
          (o) => o.deliveryPersonId === employeeId && (o.status === "assigned" || o.status === "picked_up")
        );
        setAssignedOrders(orders);
      }
    } catch (err) {
      console.error("Failed to fetch active orders", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPooledOrders = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/sales/pooled`, {
        headers: {
          'X-Company-Id': selectedEmployee?.company || '',
          'X-Branch-Id': selectedEmployee?.branch || ''
        }
      });
      if (Array.isArray(response.data)) {
        setPooledOrders(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch pooled orders", err);
    }
  };

  const fetchRestaurantLocation = async (emp) => {
    try {
      const response = await axios.get(`${baseUrl}/api/company-details`, {
        headers: {
          'X-Company-Name': emp?.company || '',
          'X-Branch-Name': emp?.branch || ''
        }
      });
      if (response.data && response.data.companyDetails && response.data.companyDetails.length > 0) {
        const details = response.data.companyDetails[0];
        if (details.addresses && details.addresses.length > 0) {
            const addr = details.addresses[0];
            if (addr.latitude && addr.longitude) {
              setRestaurantLocation({ lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) });
            }
        }
      }
    } catch (err) {
      console.error("Failed to fetch restaurant location", err);
    }
  };

  const handleClaimOrder = async (invoice_no) => {
    try {
      const response = await axios.post(`${baseUrl}/api/sales/${invoice_no}/claim_delivery`, {
        deliveryPersonId: selectedEmployee.employeeId,
        deliveryPersonName: selectedEmployee.name
      }, {
        headers: {
          'X-Company-Id': selectedEmployee?.company || '',
          'X-Branch-Id': selectedEmployee?.branch || ''
        }
      });
      if (response.status === 200) {
        setWarningMessage("Order Claimed Successfully! Opening route to Restaurant...");
        setWarningType("success");
        fetchPooledOrders();
        fetchOrders(selectedEmployee.employeeId);
        setActiveTab('assigned');
        
        setTimeout(() => {
            setSelectedMapAddress("Restaurant Location");
            // If restaurant coordinates are missing, fallback to driver's location to prevent crash
            setSelectedMapExactLocation(restaurantLocation || currentDriverLocation); 
            setShowMapModal(true);
        }, 500);
      }
    } catch (err) {
      console.error("Error claiming order:", err);
      setWarningMessage(err.response?.data?.error || "Failed to claim order.");
      setWarningType("warning");
      fetchPooledOrders();
    }
  };

  const fetchDeliveredOrders = async (employeeId) => {
    try {
      setDeliveredLoading(true);
      const response = await axios.get(`${baseUrl}/api/tripreports/${employeeId}`);
      if (Array.isArray(response.data)) {
        setDeliveredOrders(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch delivered orders", err);
    } finally {
      setDeliveredLoading(false);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return "No address provided";
    if (typeof addr === "string") return addr;
    const parts = [
      addr.flat_villa_no,
      addr.building_name,
      addr.field3,
      addr.field2,
      addr.field1,
      addr.country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "No address provided";
  };

  const getOrderGrandTotal = (order) => {
    let gt = Number(order.grandTotal || order.grand_total || order.total || order.totalAmount || 0);
    if (gt > 0) return gt.toFixed(2);

    if (Array.isArray(order.cartItems)) {
      let sub = order.cartItems.reduce((sum, item) => sum + ((Number(item.exclTotal) || 0) * (Number(item.quantity) || 1)), 0);
      let vat = order.cartItems.reduce((sum, item) => sum + ((Number(item.taxTotal) || 0) * (Number(item.quantity) || 1)), 0);
      let total = sub + vat;
      if (total === 0) {
        total = order.cartItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      }
      return total.toFixed(2);
    }
    return "0.00";
  };

  const handleAuthenticate = async (e) => {
    e.preventDefault();
    if (!secretKey.trim()) {
      setError("Please enter your secret key.");
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/api/employees/validate-secret-key`, {
        secretKey: secretKey.trim()
      });

      if (response.data && response.data.employeeId) {
        const empId = response.data.employeeId;
        
        if (user && (user.role === 'Delivery Boy' || user.role === 'delivery boy')) {
            const loggedInId = user.employeeId || user.userId || user._id;
            if (loggedInId && String(loggedInId) !== String(empId)) {
                setError("Access Denied: You can only use your own Secret Key.");
                return;
            }
        }

        setSelectedEmployee(response.data);
        setIsAuthenticated(true);
        setError("");
        fetchOrders(response.data.employeeId);
        // Also fetch pooled orders and restaurant location immediately
        setTimeout(() => {
            fetchPooledOrders();
            fetchRestaurantLocation(response.data);
        }, 100);
      }
    } catch (err) {
      console.error("Authentication error:", err);
      setError(err.response?.data?.error || "Invalid Secret Key! No matching delivery person found.");
    }
  };

  const handleCompleteOrder = (order) => {
    if (!order.paid) {
      setCurrentOrderForCash(order);
      setCashAmount(getOrderGrandTotal(order));
      setShowCashModal(true);
    } else {
      setPendingAction({ type: 'complete', orderId: order.orderId });
      setWarningMessage("Are you sure you want to mark this order as Delivered?");
      setWarningType("warning");
    }
  };

  const handleConfirmCash = async () => {
    if (!currentOrderForCash) return;
    try {
        const payload = {
            employeeId: selectedEmployee.employeeId,
            secretKey: secretKey,
            cashCollected: cashAmount
        };
        const response = await axios.post(`${baseUrl}/api/activeorders/${currentOrderForCash.orderId}/delivery-complete`, payload);
        if (response.data.success) {
          setWarningMessage("Order successfully marked as Delivered and Cash Collected!");
          setWarningType("success");
          fetchOrders(selectedEmployee.employeeId);
          setShowCashModal(false);
          setCurrentOrderForCash(null);
        } else {
          setWarningMessage(response.data.message || "Failed to complete order.");
          setWarningType("warning");
        }
    } catch (err) {
        console.error(err);
        setWarningMessage("Error processing request.");
        setWarningType("warning");
    }
  };

  const handlePickupOrder = (orderId) => {
    setPendingAction({ type: 'pickup', orderId });
    setWarningMessage("Are you sure you want to Pick Up this order? Live tracking will begin.");
    setWarningType("warning");
  };

  const confirmCompleteOrder = async () => {
    if (!pendingAction) return;
    const { type, orderId } = pendingAction;
    setPendingAction(null);
    try {
      const payload = {
        employeeId: selectedEmployee.employeeId,
        secretKey: secretKey
      };

      if (type === 'pickup') {
        // Grab current location for pickup point
        let pickupLat = null;
        let pickupLng = null;

        if (currentDriverLocation) {
          pickupLat = currentDriverLocation.lat;
          pickupLng = currentDriverLocation.lng;
        } else {
          console.warn("Current driver location not available instantly.");
        }

        if (pickupLat && pickupLng) {
          payload.pickupLat = pickupLat;
          payload.pickupLng = pickupLng;
        }

        const response = await axios.post(`${baseUrl}/api/activeorders/${orderId}/delivery-pickup`, payload);
        if (response.data.success) {
          setWarningMessage("Order Picked Up! Live tracking started.");
          setWarningType("success");
          fetchOrders(selectedEmployee.employeeId);

          // Auto-open live tracking map
          const pickedUpOrder = assignedOrders.find(o => o.orderId === orderId);
          if (pickedUpOrder) {
            setSelectedMapAddress(formatAddress(pickedUpOrder.deliveryAddress));
            setSelectedMapExactLocation(getCustomerCoordinates(pickedUpOrder));
            setActiveOrderIdForMap(orderId);
            setShowMapModal(true);
          }
        } else {
          setWarningMessage(response.data.message || "Failed to pick up order.");
          setWarningType("warning");
        }
      } else if (type === 'complete') {
        const response = await axios.post(`${baseUrl}/api/activeorders/${orderId}/delivery-complete`, payload);
        if (response.data.success) {
          setWarningMessage("Order successfully marked as Delivered!");
          setWarningType("success");
          fetchOrders(selectedEmployee.employeeId);
        } else {
          setWarningMessage(response.data.message || "Failed to complete order.");
          setWarningType("warning");
        }
      }
    } catch (err) {
      console.error("Error processing order action:", err);
      setWarningMessage("Error processing request.");
      setWarningType("warning");
    }
  };

  const handlePrint = (order) => {
    const effectivePrintSettings = printSettings || {
      restaurantName: "Restaurant",
      street: "Address",
      city: "",
      phone: "Phone",
      gstin: "",
      thankYouMessage: "Thank You",
      poweredBy: "manoj"
    };

    const headerHtml = (effectivePrintSettings.headerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');
    const footerHtml = (effectivePrintSettings.footerBlocks || []).map(b => `<div style="text-align: ${b.align};">${b.text || ''}</div>`).join('');

    const formatPrice = (amt) => "₹" + Number(amt).toFixed(2);

    let subtotal = 0;
    let taxTotal = 0;

    let itemsHtml = "";
    const cartItems = order.cartItems || [];

    cartItems.forEach(item => {
      let base = Number(item.basePrice || item.price || 0);
      let qty = Number(item.quantity || 1);
      let itemTotal = base * qty;

      itemsHtml += `
          <tr>
            <td style="text-align: left; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px; vertical-align: top;">${item.item_name || item.name}</td>
            <td style="text-align: center; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${qty}</td>
            <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatPrice(base)}</td>
            <td style="text-align: right; padding: 4px 8px; border-bottom: 1px solid #000; line-height: 1.2; font-size: 12px;">${formatPrice(itemTotal)}</td>
          </tr>
        `;

      subtotal += itemTotal;
      taxTotal += Number(item.taxTotal || item.tax_amount || 0);

      if (item.addonQuantities) {
        Object.entries(item.addonQuantities).forEach(([addonName, addonQty]) => {
          let aPrice = item.addonSizePrices ? Number(item.addonSizePrices[addonName] || 0) : 0;
          let aQty = Number(addonQty);
          let aTotal = aPrice * aQty;
          if (aQty > 0) {
            itemsHtml += `
                    <tr>
                      <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${addonName}</td>
                      <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${aQty}</td>
                      <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(aPrice)}</td>
                      <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(aTotal)}</td>
                    </tr>
                  `;
            subtotal += aTotal;
          }
        });
      }
      if (item.comboQuantities) {
        Object.entries(item.comboQuantities).forEach(([comboName, comboQty]) => {
          let cPrice = item.comboSizePrices ? Number(item.comboSizePrices[comboName] || 0) : 0;
          let cQty = Number(comboQty);
          let cTotal = cPrice * cQty;
          if (cQty > 0) {
            itemsHtml += `
                    <tr>
                      <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${comboName}</td>
                      <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${cQty}</td>
                      <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(cPrice)}</td>
                      <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(cTotal)}</td>
                    </tr>
                  `;
            subtotal += cTotal;
          }
        });
      }

      if (item.customVariantsDetails && Object.keys(item.customVariantsDetails).length > 0) {
        Object.entries(item.customVariantsDetails).forEach(([variantName, variant]) => {
          let vQty = Number(item.customVariantsQuantities?.[variantName] || 1);
          let vPrice = Number(variant.price || 0);
          let vTotal = vQty * vPrice;
          itemsHtml += `
                  <tr>
                    <td style="text-align: left; padding: 2px 8px 2px 16px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px; color: #666; vertical-align: top;">+ ${variant.heading}: ${variant.name}</td>
                    <td style="text-align: center; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${vQty}</td>
                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(vPrice)}</td>
                    <td style="text-align: right; padding: 2px 8px; border-bottom: 1px solid #eee; line-height: 1.2; font-size: 11px;">${formatPrice(vTotal)}</td>
                  </tr>
                `;
          subtotal += vTotal;
        });
      }
    });

    let deliveryCharge = Number(order.deliveryCharge || order.delivery_charge || 0);
    let packingCharge = Number(order.packingCharge || order.packing_charge || 0);

    let grandTotal = subtotal + taxTotal + deliveryCharge + packingCharge;
    grandTotal = Number(order.grandTotal || order.grand_total || order.total || order.totalAmount || grandTotal);

    const formattedDate = new Date(order.timestamp || order.created_at || Date.now()).toLocaleDateString();
    const formattedTime = new Date(order.timestamp || order.created_at || Date.now()).toLocaleTimeString();

    const deliveryAddressHtml = formatAddress(order.deliveryAddress) !== "No address provided" ? formatAddress(order.deliveryAddress) : null;

    const content = `
      <div style="font-family: Arial, sans-serif; width: 88mm; font-size: 12px; padding: 10px; color: #000000; box-sizing: border-box; line-height: 1.2;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px;">
          <div style="flex: 1; font-family: Arial, sans-serif; font-size: 12px; width: 100%;">
            ${headerHtml ? headerHtml : `
                <div style="text-align: center; margin-bottom: 10px;">
                  <h3 style="margin: 0 0 5px 0; font-size: 16px;">${effectivePrintSettings.restaurantName}</h3>
                  <p style="margin: 2px 0;">${effectivePrintSettings.street}, ${effectivePrintSettings.city}</p>
                  <p style="margin: 2px 0;">Phone: ${effectivePrintSettings.phone}</p>
                </div>
            `}
          </div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="width: 50%; text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Invoice No</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="width: 50%; text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${order.orderNo || order.orderId || order.invoice_no || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Customer</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${order.customerName || order.customer || "N/A"}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Phone</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; word-break: break-all;">${order.phoneNumber || order.phone || "N/A"}</td>
            </tr>
            ${deliveryAddressHtml ? `
              <tr>
                <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; vertical-align: top;">Delivery Address</td>
                <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
                <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: pre-line; word-break: break-all;">${deliveryAddressHtml}</td>
              </tr>
            ` : ""}
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Date</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedDate}</td>
            </tr>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Time</td>
              <td style="text-align: center; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; white-space: nowrap;">${formattedTime}</td>
            </tr>
          </tbody>
        </table>

        <table style="width: 100%; margin-bottom: 10px; border-collapse: collapse; border: 1px solid #000000; table-layout: fixed;">
          <thead>
            <tr style="border-bottom: 1px dashed #000000;">
              <th style="text-align: left; width: 40%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Item</th>
              <th style="text-align: center; width: 15%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Qty</th>
              <th style="text-align: right; width: 20%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Price</th>
              <th style="text-align: right; width: 25%; padding: 4px 8px; border: none; font-size: 12px; font-weight: bold;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>
        
        <table style="width: 100%; border-collapse: collapse; border: none; margin-bottom: 10px;">
          <tbody>
            <tr>
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">Subtotal:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px; font-weight: bold;">${formatPrice(subtotal)}</td>
            </tr>
            ${taxTotal > 0 ? `
              <tr>
                <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Total VAT:</td>
                <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatPrice(taxTotal)}</td>
              </tr>
            ` : ""}
            ${deliveryCharge > 0 ? `
              <tr>
                <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Delivery Charge:</td>
                <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatPrice(deliveryCharge)}</td>
              </tr>
            ` : ""}
            ${packingCharge > 0 ? `
              <tr>
                <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">Packing Charge:</td>
                <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 12px;">${formatPrice(packingCharge)}</td>
              </tr>
            ` : ""}
            <tr style="font-weight: bold; border-top: 2px solid #000;">
              <td style="text-align: left; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">Grand Total:</td>
              <td style="text-align: right; padding: 4px 0; border: none; line-height: 1.2; font-size: 14px;">${formatPrice(grandTotal)}</td>
            </tr>
          </tbody>
        </table>

        <div style="margin-top: 15px; width: 100%;">
          ${footerHtml ? footerHtml : `
              <div style="text-align: center;">
                <p style="margin: 2px 0;">${effectivePrintSettings.thankYouMessage}</p>
                <p style="margin: 2px 0;">Powered by ${effectivePrintSettings.poweredBy}</p>
              </div>
          `}
        </div>
      </div>
    `;

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Print Receipt</title>
        </head>
        <body onload="window.print(); window.close();" style="margin:0; padding:0;">
          ${content}
        </body>
      </html>
    `);
    win.document.close();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setSecretKey("");
    setSelectedEmployee(null);
    setAssignedOrders([]);
    setDeliveredOrders([]);
    setFilterDate("");
    setFilterMonth("");
    setFilterYear(new Date().getFullYear().toString());
    setFilterInvoiceNo("");
    setActiveTab('assigned');
  };

  if (!isAuthenticated) {
    return (
      <div className="main-customer-page" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <div style={{ width: "100%", maxWidth: "450px", background: "white", padding: "40px", borderRadius: "16px", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <FaLock style={{ fontSize: "3rem", color: "#3b82f6", marginBottom: "15px" }} />
            <h2 style={{ margin: 0, fontSize: "1.8rem", color: "#1e293b", fontWeight: "700" }}>Delivery Portal</h2>
            <p style={{ color: "#64748b", marginTop: "5px" }}>Log in to view assigned orders</p>
          </div>

          <form onSubmit={handleAuthenticate}>
            {error && (
              <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "10px", borderRadius: "8px", marginBottom: "20px", fontSize: "0.9rem", textAlign: "center" }}>
                {error}
              </div>
            )}



            <div style={{ marginBottom: "30px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#475569", fontWeight: "600" }}>Secret Key</label>
              <div style={{ position: "relative" }}>
                <FaKey style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                <input
                  type="password"
                  placeholder="Enter contact number or PIN"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  style={{ width: "100%", padding: "12px 12px 12px 40px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "1rem", boxSizing: "border-box" }}
                />
              </div>
            </div>

            <button
              type="submit"
              style={{ width: "100%", background: "#3b82f6", color: "white", border: "none", padding: "14px", borderRadius: "8px", fontSize: "1.1rem", fontWeight: "600", cursor: "pointer", transition: "background 0.2s" }}
              onMouseOver={(e) => (e.target.style.background = "#2563eb")}
              onMouseOut={(e) => (e.target.style.background = "#3b82f6")}
            >
              Access Portal
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="main-customer-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: "100vh", background: "#f8fafc" }}>
      <style>
        {`
          .premium-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            background: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }
          .premium-table th {
            background: #06D6A0;
            color: #ffffff;
            font-weight: 600;
            font-size: 15px;
            text-transform: capitalize;
            letter-spacing: 0.3px;
            padding: 16px 24px;
            text-align: left;
            border: none;
            position: sticky;
            top: 0;
            z-index: 10;
          }
          .premium-table th.right-align {
            text-align: right;
          }
          .premium-table th.center-align {
            text-align: center;
          }
          .premium-table th:first-child {
            border-top-left-radius: 10px;
          }
          .premium-table th:last-child {
            border-top-right-radius: 10px;
          }
          .premium-table td {
            padding: 16px 24px;
            color: #4A4A5A;
            font-size: 14px;
            font-weight: 500;
            border-bottom: 1px solid #F1F3F8;
            transition: all 0.2s ease;
            vertical-align: middle;
          }
          .premium-table tr:nth-child(odd) td {
            background: #ffffff;
          }
          .premium-table tr:nth-child(even) td {
            background: #FAFBFD;
          }
          .premium-table tr:hover td {
            background: #F1F4F9;
          }
          .premium-table tr:last-child td {
            border-bottom: none;
          }
        `}
      </style>

      {/* Top Bar Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'transparent',
        padding: '36px 48px 16px 48px',
        width: '100%',
        boxSizing: 'border-box',
        flex: '0 0 auto'
      }}>
        {/* Left Side: Back Button */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <button
            onClick={handleLogout}
            style={{
              height: '44px',
              padding: '0 20px',
              borderRadius: '10px',
              border: '1px solid #E2E6EF',
              background: '#ffffff',
              color: '#604BE8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.background = '#eff2f9'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <FaArrowLeft style={{ fontSize: '12px' }} /> Back
          </button>
        </div>

        {/* Center: Title */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>{selectedEmployee.name}</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px", fontWeight: '500' }}>Delivery Dashboard</p>
        </div>

        {/* Right Side: Action Buttons */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (activeTab === 'assigned') fetchOrders(selectedEmployee.employeeId);
              else if (activeTab === 'delivered') fetchDeliveredOrders(selectedEmployee.employeeId);
              else if (activeTab === 'pool') fetchPooledOrders();
            }}
            style={{
              height: '44px',
              padding: '0 16px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #E2E6EF',
              color: '#4A4A5A',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#eff2f9'; e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.color = '#604BE8'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.color = '#4A4A5A'; }}
          >
            Refresh
          </button>

          <div style={{ display: 'flex', background: '#F1F3F8', padding: '4px', borderRadius: '12px' }}>
            <button
              onClick={() => { setActiveTab('pool'); fetchPooledOrders(); }}
              style={{
                height: '36px', padding: '0 16px', border: 'none', borderRadius: '8px',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                backgroundColor: activeTab === 'pool' ? '#ffffff' : 'transparent',
                color: activeTab === 'pool' ? '#604BE8' : '#64748B',
                boxShadow: activeTab === 'pool' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Order Pool
            </button>
            <button
              onClick={() => { setActiveTab('assigned'); fetchOrders(selectedEmployee.employeeId); }}
              style={{
                height: '36px', padding: '0 16px', border: 'none', borderRadius: '8px',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                backgroundColor: activeTab === 'assigned' ? '#ffffff' : 'transparent',
                color: activeTab === 'assigned' ? '#604BE8' : '#64748B',
                boxShadow: activeTab === 'assigned' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Assigned
            </button>
            <button
              onClick={() => { setActiveTab('delivered'); fetchDeliveredOrders(selectedEmployee.employeeId); }}
              style={{
                height: '36px', padding: '0 16px', border: 'none', borderRadius: '8px',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s',
                backgroundColor: activeTab === 'delivered' ? '#ffffff' : 'transparent',
                color: activeTab === 'delivered' ? '#604BE8' : '#64748B',
                boxShadow: activeTab === 'delivered' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Delivered
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, padding: "0 48px 48px 48px" }}>

        {activeTab === 'pool' && (
          <>
            {pooledOrders.length === 0 ? (
              <div style={{ background: "white", padding: "60px 20px", textAlign: "center", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                <FaBoxOpen size={48} style={{ color: "#cbd5e1", marginBottom: "20px" }} />
                <h3 style={{ margin: 0, color: "#334155", fontSize: "1.3rem" }}>Order Pool is Empty</h3>
                <p style={{ color: "#64748b", marginTop: "10px" }}>Wait for new online delivery orders to be broadcasted here.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "20px" }}>
                {pooledOrders.map((order) => {
                  return (
                    <div key={order.orderId || order._id} style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", borderTop: "4px solid #10b981", display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ background: "#d1fae5", color: "#047857", padding: "4px 8px", borderRadius: "6px", fontSize: "0.8rem", fontWeight: "700" }}>
                              #{order.orderNo || order.orderId}
                            </span>
                            {(() => {
                              const kStatus = getOrderKitchenStatus(order);
                              let bg = "#f1f5f9", clr = "#64748b";
                              if (kStatus === "Ready") { bg = "#dcfce7"; clr = "#166534"; }
                              else if (kStatus === "Preparing") { bg = "#fef9c3"; clr = "#854d0e"; }
                              return (
                                <span style={{ background: bg, color: clr, padding: "4px 8px", borderRadius: "6px", fontSize: "0.75rem", fontWeight: "700", border: `1px solid ${clr}33` }}>
                                  Kitchen: {kStatus}
                                </span>
                              );
                            })()}
                          </div>
                          <h3 style={{ margin: "10px 0 5px", fontSize: "1.2rem", color: "#0f172a" }}>{order.customerName || order.customer || "N/A"}</h3>
                          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>Phone: {order.phoneNumber || order.phone || "N/A"}</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: "bold", color: "#0f172a", fontSize: "1.2rem" }}>₹{getOrderGrandTotal(order)}</div>
                          <div style={{ color: order.paid ? "#10b981" : "#f59e0b", fontSize: "0.85rem", fontWeight: "600", marginTop: "4px" }}>
                            {order.paid ? "PAID" : "UNPAID (CASH)"}
                          </div>
                        </div>
                      </div>

                      <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: "0.85rem", color: "#475569", lineHeight: "1.5", flex: 1 }}>
                          <strong>Delivery Address:</strong><br />
                          {formatAddress(order.deliveryAddress)}
                        </p>
                      </div>

                      <button
                        onClick={() => handleClaimOrder(order.invoice_no || order.orderId || order._id)}
                        style={{ width: "100%", background: "#10b981", color: "white", border: "none", padding: "14px", borderRadius: "8px", fontSize: "1.1rem", fontWeight: "bold", cursor: "pointer", transition: "background 0.2s" }}
                        onMouseOver={(e) => (e.target.style.background = "#059669")}
                        onMouseOut={(e) => (e.target.style.background = "#10b981")}
                      >
                        Accept Order
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'assigned' && (
          <>
            {loading ? (
              <div style={{ textAlign: "center", padding: "50px", color: "#64748b" }}>Loading assigned orders...</div>
            ) : assignedOrders.length === 0 ? (
              <div style={{ background: "white", padding: "60px 20px", textAlign: "center", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                <FaBoxOpen size={48} style={{ color: "#cbd5e1", marginBottom: "20px" }} />
                <h3 style={{ margin: 0, color: "#334155", fontSize: "1.3rem" }}>No Assigned Orders</h3>
                <p style={{ color: "#64748b", marginTop: "10px" }}>You currently have no orders waiting for delivery.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "20px" }}>
                {assignedOrders.map((order) => {
                  const customerLoc = getCustomerCoordinates(order) || resolvedCustomerLocations[order.orderId];
                  const distance = calculateDistance(currentDriverLocation, customerLoc);
                  const isWithin100m = distance !== null && distance <= 100;

                  return (
                    <div key={order.orderId} style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.05)", borderTop: "4px solid #3b82f6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" }}>
                        <div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "4px 8px", borderRadius: "6px", fontSize: "0.8rem", fontWeight: "700" }}>
                              #{order.orderNo || order.orderId}
                            </span>
                            {(() => {
                              const kStatus = getOrderKitchenStatus(order);
                              let bg = "#f1f5f9", clr = "#64748b";
                              if (kStatus === "Ready") { bg = "#dcfce7"; clr = "#166534"; }
                              else if (kStatus === "Preparing") { bg = "#fef9c3"; clr = "#854d0e"; }
                              return (
                                <span style={{ background: bg, color: clr, padding: "4px 8px", borderRadius: "6px", fontSize: "0.75rem", fontWeight: "700", border: `1px solid ${clr}33` }}>
                                  Kitchen: {kStatus}
                                </span>
                              );
                            })()}
                          </div>
                          <h3 style={{ margin: "10px 0 5px", fontSize: "1.2rem", color: "#0f172a" }}>{order.customerName || order.customer || "N/A"}</h3>
                          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>Phone: {order.phoneNumber || "N/A"}</p>
                          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>Email: {order.email || "N/A"}</p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: "bold", color: "#0f172a", fontSize: "1.2rem" }}>₹{order.grandTotal}</div>
                          <div style={{ color: order.paid ? "#10b981" : "#f59e0b", fontSize: "0.85rem", fontWeight: "600", marginTop: "4px" }}>
                            {order.paid ? "PAID" : "UNPAID (CASH)"}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginBottom: "15px" }}>
                        <h4 style={{ margin: "0 0 10px 0", color: "#1e293b", fontSize: "0.95rem" }}>Order Items:</h4>
                        {order.cartItems?.map((item, idx) => (
                          <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", fontSize: "0.85rem", color: "#475569" }}>
                            <span>{item.quantity}x {item.name}</span>
                            <span>₹{((item.basePrice || 0) * (item.quantity || 1)).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: "0.85rem", color: "#475569", lineHeight: "1.5", flex: 1 }}>
                          <strong>Delivery Address:</strong><br />
                          {formatAddress(order.deliveryAddress)}
                        </p>
                        <button
                          onClick={() => {
                            if (order.status === 'picked_up') {
                                setSelectedMapAddress(formatAddress(order.deliveryAddress));
                                setSelectedMapExactLocation(getCustomerCoordinates(order));
                                setActiveOrderIdForMap(order.orderId);
                            } else {
                                setSelectedMapAddress("Restaurant Location");
                                setSelectedMapExactLocation(restaurantLocation);
                                setActiveOrderIdForMap(null);
                            }
                            setShowMapModal(true);
                          }}
                          style={{ marginLeft: "10px", background: "#e0f2fe", color: "#0284c7", border: "1px solid #bae6fd", padding: "8px 12px", borderRadius: "6px", fontSize: "0.85rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap" }}
                        >
                          <FaMapMarkerAlt /> {order.status === 'picked_up' ? 'Customer Route' : 'Restaurant Route'}
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          onClick={() => handlePrint(order)}
                          style={{ flex: 1, background: "#f8fafc", color: "#475569", border: "1px solid #cbd5e1", padding: "12px", borderRadius: "8px", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                        >
                          <FaPrint /> Print Bill
                        </button>

                        {order.status === "assigned" ? (
                          <button
                            onClick={() => handlePickupOrder(order.orderId)}
                            style={{ flex: 2, background: "#3b82f6", color: "white", border: "none", padding: "12px", borderRadius: "8px", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background 0.2s" }}
                            onMouseOver={(e) => (e.target.style.background = "#2563eb")}
                            onMouseOut={(e) => (e.target.style.background = "#3b82f6")}
                          >
                            <FaCheckCircle /> Pick Up Order
                          </button>
                        ) : (
                          isWithin100m ? (
                            <button
                              onClick={() => handleCompleteOrder(order)}
                              style={{ flex: 2, background: "#10b981", color: "white", border: "none", padding: "12px", borderRadius: "8px", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background 0.2s" }}
                              onMouseOver={(e) => (e.target.style.background = "#059669")}
                              onMouseOut={(e) => (e.target.style.background = "#10b981")}
                            >
                              <FaCheckCircle /> Mark as Delivered
                            </button>
                          ) : (
                            <div style={{ flex: 2, background: "#f1f5f9", color: "#64748b", border: "1px dashed #cbd5e1", padding: "12px", borderRadius: "8px", fontSize: "0.9rem", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                              <FaExclamationTriangle />
                              {distance === null ? "Location unknown" : `Too far (${Math.round(distance)}m)`}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'delivered' && (
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.3rem' }}>Delivered Orders</h3>
              <button
                onClick={() => {
                  setActiveTab('assigned');
                  fetchOrders(selectedEmployee.employeeId);
                }}
                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Cancel / Close
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px', background: '#f8fafc', padding: '15px', borderRadius: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '5px', fontWeight: 'bold' }}>Date</label>
                <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '5px', fontWeight: 'bold' }}>Month</label>
                <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                  <option value="">All</option>
                  <option value="0">January</option>
                  <option value="1">February</option>
                  <option value="2">March</option>
                  <option value="3">April</option>
                  <option value="4">May</option>
                  <option value="5">June</option>
                  <option value="6">July</option>
                  <option value="7">August</option>
                  <option value="8">September</option>
                  <option value="9">October</option>
                  <option value="10">November</option>
                  <option value="11">December</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '5px', fontWeight: 'bold' }}>Year</label>
                <input type="number" placeholder="YYYY" value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '5px', fontWeight: 'bold' }}>Invoice No</label>
                <input type="text" placeholder="Search Invoice..." value={filterInvoiceNo} onChange={e => setFilterInvoiceNo(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }} />
              </div>
            </div>

            {deliveredLoading ? (
              <div style={{ textAlign: "center", padding: "50px", color: "#64748b" }}>Loading delivered orders...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {(() => {
                  const filtered = deliveredOrders.filter(order => {
                    let match = true;
                    const orderDate = new Date(order.timestamp || order.created_at);
                    if (filterDate) {
                      const d1 = new Date(filterDate).toDateString();
                      const d2 = orderDate.toDateString();
                      if (d1 !== d2) match = false;
                    }
                    if (filterMonth !== "") {
                      if (orderDate.getMonth().toString() !== filterMonth) match = false;
                    }
                    if (filterYear) {
                      if (orderDate.getFullYear().toString() !== filterYear) match = false;
                    }
                    if (filterInvoiceNo) {
                      const invoiceNo = (order.orderNo || order.orderId || "").toLowerCase();
                      if (!invoiceNo.includes(filterInvoiceNo.toLowerCase())) match = false;
                    }
                    return match;
                  });

                  if (filtered.length === 0) return <div style={{ textAlign: 'center', color: '#64748b', padding: '30px' }}>No orders found matching the filters.</div>;

                  return (
                    <table className="premium-table">
                      <thead>
                        <tr>
                          <th>Invoice No</th>
                          <th>Date & Time</th>
                          <th>Customer</th>
                          <th className="right-align">Amount</th>
                          <th className="right-align">Cash Collected</th>
                          <th className="center-align">Status</th>
                          <th className="center-align">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((order, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 'bold', color: '#0f172a' }}>#{order.orderNo || order.orderId}</td>
                            <td>{new Date(order.timestamp || order.created_at).toLocaleString()}</td>
                            <td>{order.customerName || order.customer || "N/A"}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>₹{getOrderGrandTotal(order)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>{order.cashCollected ? `₹${parseFloat(order.cashCollected).toFixed(2)}` : "-"}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{ color: '#10b981', fontSize: '0.75rem', fontWeight: 'bold', background: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>DELIVERED</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => setSelectedOrderDetails(order)}
                                style={{
                                  background: '#e0f2fe',
                                  color: '#0ea5e9',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {warningMessage && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div className="premium-modal-box" style={{
            background: '#ffffff',
            borderRadius: '24px',
            padding: '40px 32px',
            width: '90%',
            maxWidth: '440px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            animation: 'modalScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '200px',
              background: warningType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}></div>

            <div className="premium-modal-icon" style={{
              width: '84px',
              height: '84px',
              borderRadius: '50%',
              background: warningType === 'success' ? '#ecfdf5' : '#fffbeb',
              color: warningType === 'success' ? '#06D6A0' : '#FF9F04',
              border: `2px solid ${warningType === 'success' ? '#a7f3d0' : '#fde68a'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '38px',
              marginBottom: '24px',
              boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)',
              zIndex: 1,
              animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            }}>
              {warningType === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
            </div>

            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1e293b',
              margin: '0 0 12px 0',
              zIndex: 1,
              fontFamily: `'Gilroy', 'Inter', sans-serif`
            }}>
              {warningType === 'success' ? "Success" : "Notice"}
            </h3>

            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.6',
              margin: '0 0 32px 0',
              zIndex: 1,
              fontWeight: '500'
            }}>
              {warningMessage}
            </p>

            <div className="premium-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              width: '100%',
              zIndex: 1
            }}>
              <button
                type="button"
                className="premium-btn-cancel"
                onClick={() => { setWarningMessage(""); setPendingAction(null); }}
                style={{
                  flex: 1,
                  height: '48px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: `'Gilroy', 'Inter', sans-serif`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="premium-btn-ok"
                onClick={() => {
                  if (pendingAction) {
                    confirmCompleteOrder();
                  } else {
                    setWarningMessage("");
                  }
                }}
                style={{
                  flex: 1,
                  height: '48px',
                  background: warningType === 'success' ? '#06D6A0' : '#604BE8',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)',
                  transition: 'all 0.2s ease',
                  fontFamily: `'Gilroy', 'Inter', sans-serif`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 12px 25px -5px rgba(6, 214, 160, 0.4)' : '0 12px 25px -5px rgba(96, 75, 232, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'; }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedOrderDetails && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999
        }}>
          <div className="premium-modal-box" style={{
            background: '#ffffff', borderRadius: '24px', padding: '30px',
            width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.5rem' }}>Order Details</h2>
              <button onClick={() => setSelectedOrderDetails(null)} style={{ background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Invoice No:</span>
                <div style={{ fontWeight: 'bold', color: '#0f172a' }}>#{selectedOrderDetails.orderNo || selectedOrderDetails.orderId}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Date & Time:</span>
                <div style={{ fontWeight: 'bold', color: '#0f172a' }}>{new Date(selectedOrderDetails.timestamp || selectedOrderDetails.created_at).toLocaleString()}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Customer:</span>
                <div style={{ fontWeight: 'bold', color: '#0f172a' }}>{selectedOrderDetails.customerName || selectedOrderDetails.customer || "N/A"}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Phone:</span>
                <div style={{ fontWeight: 'bold', color: '#0f172a' }}>{selectedOrderDetails.phoneNumber || "N/A"}</div>
              </div>
            </div>

            <div style={{ background: "#f8fafc", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Items</h4>
              {selectedOrderDetails.cartItems?.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                  <div>
                    <span style={{ fontWeight: '600', color: '#334155' }}>{item.quantity}x {item.name}</span>
                  </div>
                  <div style={{ fontWeight: '600', color: '#0f172a' }}>₹{((item.basePrice || 0) * (item.quantity || 1)).toFixed(2)}</div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', paddingTop: '10px', borderTop: '2px solid #cbd5e1' }}>
                <strong style={{ color: '#0f172a' }}>Grand Total:</strong>
                <strong style={{ color: '#10b981', fontSize: '1.2rem' }}>₹{getOrderGrandTotal(selectedOrderDetails)}</strong>
              </div>
            </div>

            <div style={{ background: "#f8fafc", padding: "15px", borderRadius: "8px" }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Delivery Address</h4>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: '1.5' }}>
                {formatAddress(selectedOrderDetails.deliveryAddress)}
              </p>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setSelectedOrderDetails(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cash Collection Modal */}
      {showCashModal && currentOrderForCash && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "20px" }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px', padding: '30px',
            width: '90%', maxWidth: '400px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#0f172a', textAlign: 'center' }}>Collect Cash</h3>
            <p style={{ margin: '0 0 15px 0', color: '#475569', textAlign: 'center', fontSize: '0.9rem' }}>
              Please enter the exact cash amount collected for Order #{currentOrderForCash.orderNo || currentOrderForCash.orderId}
            </p>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "#334155" }}>Cash Received (₹)</label>
              <input 
                type="number" 
                value={cashAmount} 
                onChange={(e) => setCashAmount(e.target.value)}
                style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "1.1rem" }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => { setShowCashModal(false); setCurrentOrderForCash(null); }}
                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCash}
                style={{ flex: 1, background: '#10b981', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Confirm & Deliver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMapModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "800px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                  <FaMapMarkerAlt style={{ color: "#ef4444" }} /> Delivery Location
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "#64748b" }}>{selectedMapAddress}</p>
              </div>
              <button
                onClick={() => setShowMapModal(false)}
                style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", borderRadius: "50%", transition: "all 0.2s" }}
                onMouseOver={(e) => { e.currentTarget.style.background = "#e2e8f0"; e.currentTarget.style.color = "#334155"; }}
                onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; }}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div style={{ padding: "24px" }}>
              <DeliveryMap 
                address={selectedMapAddress}
                exactLocation={selectedMapExactLocation}
                driverLocation={currentDriverLocation}
                onDriverMarkerDragEnd={(newLoc) => {
                    isSimulatingRef.current = true;
                    setCurrentDriverLocation(newLoc);
                }}
                onDestinationResolved={(loc) => {
                    if (activeOrderIdForMap) {
                        setResolvedCustomerLocations(prev => ({ ...prev, [activeOrderIdForMap]: loc }));
                    }
                }}
                height="500px" 
              />
          </div>
        </div>
        </div>
      )}

      {/* New Order Alert Modal */}
      {newOrderAlert && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px', padding: '40px 32px',
            width: '90%', maxWidth: '440px', textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative', overflow: 'hidden'
          }}>
            <div style={{
              width: '84px', height: '84px', borderRadius: '50%', background: '#ecfdf5', color: '#06D6A0',
              border: '2px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '32px', margin: '0 auto 24px auto', boxShadow: '0 0 20px rgba(6, 214, 160, 0.2)'
            }}>
              <FaBoxOpen />
            </div>
            
            <h2 style={{ margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800', color: '#1B1B29' }}>
              New Order Broadcasted!
            </h2>
            <p style={{ margin: '0 0 24px 0', fontSize: '15px', color: '#4A4A5A', lineHeight: '1.6' }}>
              Order <strong style={{ color: '#06D6A0' }}>#{newOrderAlert.orderNo || newOrderAlert.invoice_no}</strong> is now available in the Order Pool. Accept it before someone else does!
            </p>

            <button
              onClick={() => setNewOrderAlert(null)}
              style={{
                width: '100%', padding: '16px', background: '#604BE8', color: 'white',
                border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold',
                cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(96, 75, 232, 0.3)'
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 75, 232, 0.4)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(96, 75, 232, 0.3)'; }}
            >
              View Order Pool
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryPanel;
